/**
 * Domain Status Mappers for ArendaPro+
 * 
 * Handles conversion between frontend (camelCase) and database (snake_case) formats
 * for all domain-specific status tables.
 * 
 * Following STYLEGUIDE.md mapping patterns:
 * - Frontend: camelCase
 * - Database: snake_case
 * - Consistent date/timestamp handling
 */

import {
  OrderStatus, OrderStatusDb, OrderStatusInsert, OrderStatusUpdate,
  RentalStatus, RentalStatusDb, RentalStatusInsert, RentalStatusUpdate,
  PaymentMethod, PaymentMethodDb, PaymentMethodInsert, PaymentMethodUpdate,
  PaymentStatus, PaymentStatusDb, PaymentStatusInsert, PaymentStatusUpdate,
  InventoryStatus, InventoryStatusDb, InventoryStatusInsert, InventoryStatusUpdate,
  InventoryCondition, InventoryConditionDb, InventoryConditionInsert, InventoryConditionUpdate,
  WorkshopStatus, WorkshopStatusDb, WorkshopStatusInsert, WorkshopStatusUpdate,
  DomainStatusOption
} from '@/types/domain-statuses';

// =============================================================================
// ORDER STATUS MAPPERS
// =============================================================================

export function orderStatusDbToClient(dbStatus: OrderStatusDb): OrderStatus {
  return {
    id: dbStatus.id,
    name: dbStatus.name,
    color: dbStatus.color,
    description: dbStatus.description || undefined,
    sortOrder: dbStatus.sort_order,
    userId: dbStatus.user_id,
    createdAt: new Date(dbStatus.created_at),
    updatedAt: new Date(dbStatus.updated_at),
  };
}

// =============================================================================
// WORKSHOP STATUS MAPPERS
// =============================================================================

export function workshopStatusDbToClient(dbStatus: WorkshopStatusDb): WorkshopStatus {
  return {
    id: dbStatus.id,
    name: dbStatus.name,
    color: dbStatus.color,
    description: dbStatus.description || undefined,
    sortOrder: dbStatus.sort_order,
    userId: dbStatus.user_id,
    createdAt: new Date(dbStatus.created_at),
    updatedAt: new Date(dbStatus.updated_at),
  };
}

export function workshopStatusClientToDbInsert(
  status: Omit<WorkshopStatus, 'id' | 'createdAt' | 'updatedAt'> & { userId: string }
): WorkshopStatusInsert {
  return {
    name: status.name,
    color: status.color,
    description: status.description,
    user_id: status.userId,
    sort_order: status.sortOrder ?? 0,
  };
}

export function workshopStatusUpdateToDb(update: WorkshopStatusUpdate): WorkshopStatusUpdate {
  return {
    name: update.name,
    color: update.color,
    description: update.description,
    sort_order: update.sort_order,
  };
}

export function orderStatusClientToDbInsert(
  status: Omit<OrderStatus, 'id' | 'createdAt' | 'updatedAt'> & { userId: string }
): OrderStatusInsert {
  return {
    name: status.name,
    color: status.color,
    description: status.description,
    user_id: status.userId,
    sort_order: status.sortOrder ?? 0,
  };
}

export function orderStatusUpdateToDb(update: OrderStatusUpdate): OrderStatusUpdate {
  return {
    name: update.name,
    color: update.color,
    description: update.description,
    sort_order: update.sort_order,
  };
}

// =============================================================================
// RENTAL STATUS MAPPERS
// =============================================================================

export function rentalStatusDbToClient(dbStatus: RentalStatusDb): RentalStatus {
  return {
    id: dbStatus.id,
    name: dbStatus.name,
    color: dbStatus.color,
    description: dbStatus.description || undefined,
    sortOrder: dbStatus.sort_order,
    userId: dbStatus.user_id,
    createdAt: new Date(dbStatus.created_at),
    updatedAt: new Date(dbStatus.updated_at),
  };
}

export function rentalStatusClientToDbInsert(
  status: Omit<RentalStatus, 'id' | 'createdAt' | 'updatedAt'> & { userId: string }
): RentalStatusInsert {
  return {
    name: status.name,
    color: status.color,
    description: status.description,
    user_id: status.userId,
    sort_order: status.sortOrder ?? 0,
  };
}

export function rentalStatusUpdateToDb(update: RentalStatusUpdate): RentalStatusUpdate {
  return {
    name: update.name,
    color: update.color,
    description: update.description,
    sort_order: update.sort_order,
  };
}

// =============================================================================
// PAYMENT METHOD MAPPERS
// =============================================================================

export function paymentMethodDbToClient(dbMethod: PaymentMethodDb): PaymentMethod {
  return {
    id: dbMethod.id,
    name: dbMethod.name,
    description: dbMethod.description || undefined,
    isActive: dbMethod.is_active,
    isDefault: dbMethod.is_default,
    config: dbMethod.config,
    userId: dbMethod.user_id,
    createdAt: new Date(dbMethod.created_at),
    updatedAt: new Date(dbMethod.updated_at),
  };
}

export function paymentMethodClientToDbInsert(
  method: Omit<PaymentMethod, 'id' | 'createdAt' | 'updatedAt'> & { userId: string }
): PaymentMethodInsert {
  return {
    name: method.name,
    description: method.description,
    is_active: method.isActive,
    is_default: method.isDefault,
    config: method.config,
    user_id: method.userId,
  };
}

export function paymentMethodUpdateToDb(update: PaymentMethodUpdate): PaymentMethodUpdate {
  const dbUpdate: PaymentMethodUpdate = {};
  
  if (update.name !== undefined) dbUpdate.name = update.name;
  if (update.description !== undefined) dbUpdate.description = update.description;
  if (update.is_active !== undefined) dbUpdate.is_active = update.is_active;
  if (update.is_default !== undefined) dbUpdate.is_default = update.is_default;
  if (update.config !== undefined) dbUpdate.config = update.config;
  
  return dbUpdate;
}

// =============================================================================
// PAYMENT STATUS MAPPERS
// =============================================================================

export function paymentStatusDbToClient(dbStatus: PaymentStatusDb): PaymentStatus {
  return {
    id: dbStatus.id,
    name: dbStatus.name,
    color: dbStatus.color,
    description: dbStatus.description || undefined,
    sortOrder: dbStatus.sort_order,
    userId: dbStatus.user_id,
    createdAt: new Date(dbStatus.created_at),
    updatedAt: new Date(dbStatus.updated_at),
  };
}

export function paymentStatusClientToDbInsert(
  status: Omit<PaymentStatus, 'id' | 'createdAt' | 'updatedAt'> & { userId: string }
): PaymentStatusInsert {
  return {
    name: status.name,
    color: status.color,
    description: status.description,
    user_id: status.userId,
    sort_order: status.sortOrder ?? 0,
  };
}

export function paymentStatusUpdateToDb(update: PaymentStatusUpdate): PaymentStatusUpdate {
  return {
    name: update.name,
    color: update.color,
    description: update.description,
    sort_order: update.sort_order,
  };
}

// =============================================================================
// INVENTORY STATUS MAPPERS
// =============================================================================

export function inventoryStatusDbToClient(dbStatus: InventoryStatusDb): InventoryStatus {
  return {
    id: dbStatus.id,
    name: dbStatus.name,
    color: dbStatus.color,
    description: dbStatus.description || undefined,
    sortOrder: dbStatus.sort_order,
    userId: dbStatus.user_id,
    createdAt: new Date(dbStatus.created_at),
    updatedAt: new Date(dbStatus.updated_at),
  };
}

export function inventoryStatusClientToDbInsert(
  status: Omit<InventoryStatus, 'id' | 'createdAt' | 'updatedAt'> & { userId: string }
): InventoryStatusInsert {
  return {
    name: status.name,
    color: status.color,
    description: status.description,
    user_id: status.userId,
    sort_order: status.sortOrder ?? 0,
  };
}

export function inventoryStatusUpdateToDb(update: InventoryStatusUpdate): InventoryStatusUpdate {
  return {
    name: update.name,
    color: update.color,
    description: update.description,
    sort_order: update.sort_order,
  };
}

// =============================================================================
// INVENTORY CONDITION MAPPERS
// =============================================================================

export function inventoryConditionDbToClient(dbCondition: InventoryConditionDb): InventoryCondition {
  return {
    id: dbCondition.id,
    name: dbCondition.name,
    color: dbCondition.color,
    description: dbCondition.description || undefined,
    sortOrder: dbCondition.sort_order,
    userId: dbCondition.user_id,
    createdAt: new Date(dbCondition.created_at),
    updatedAt: new Date(dbCondition.updated_at),
  };
}

export function inventoryConditionClientToDbInsert(
  condition: Omit<InventoryCondition, 'id' | 'createdAt' | 'updatedAt'> & { userId: string }
): InventoryConditionInsert {
  return {
    name: condition.name,
    color: condition.color,
    description: condition.description,
    user_id: condition.userId,
    sort_order: condition.sortOrder ?? 0,
  };
}

export function inventoryConditionUpdateToDb(update: InventoryConditionUpdate): InventoryConditionUpdate {
  return {
    name: update.name,
    color: update.color,
    description: update.description,
    sort_order: update.sort_order,
  };
}

// =============================================================================
// UTILITY MAPPERS FOR SELECTORS
// =============================================================================

export function statusToOption<T extends { id: string; name: string; color?: string }>(
  status: T,
  useNameAsValue: boolean = false
): DomainStatusOption {
  return {
    id: status.id,
    name: status.name,
    color: status.color,
    value: useNameAsValue ? status.name : status.id,
    label: status.name,
  };
}

export function statusesToOptions<T extends { id: string; name: string; color?: string }>(
  statuses: T[],
  useNameAsValue: boolean = false
): DomainStatusOption[] {
  return statuses.map(status => statusToOption(status, useNameAsValue));
}

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

export function validateStatusName(name: string): boolean {
  return name.trim().length > 0 && name.trim().length <= 100;
}

export function validateColorCode(color: string): boolean {
  const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexColorRegex.test(color);
}

export function sanitizeStatusInput(input: {
  name?: string;
  color?: string;
  description?: string;
}): {
  name?: string;
  color?: string;
  description?: string;
} {
  const sanitized: { name?: string; color?: string; description?: string } = {};
  
  if (input.name !== undefined) {
    sanitized.name = input.name.trim();
  }
  
  if (input.color !== undefined) {
    sanitized.color = input.color.trim().toLowerCase();
  }
  
  if (input.description !== undefined) {
    sanitized.description = input.description.trim() || undefined;
  }
  
  return sanitized;
}
